import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { RefreshCw, ScanLine, Package, ArrowRight, Search } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';
import BarcodeScannerModal from './BarcodeScannerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOTIVI = ['Guasto', 'Fine vita', 'Aggiornamento modello', 'Riparazione in laboratorio', 'Altro'];

/**
 * Dialog "Sostituisci cassa" su un impianto:
 * - Scansiona (o digita) la matricola/EAN della NUOVA cassa
 * - Se trovata in magazzino → assegnazione automatica (scarico) + vecchia resa
 * - Se non in magazzino → inserimento manuale matricola/marca/modello
 */
export default function SostituisciCassaDialog({ open, onOpenChange, impianto, onDone }) {
  const [codiceRicerca, setCodiceRicerca] = useState('');
  const [nuovoArticolo, setNuovoArticolo] = useState(null); // articolo trovato in magazzino
  const [manuale, setManuale] = useState(false);
  const [manualeForm, setManualeForm] = useState({ matricola: '', marca: '', modello: '' });
  const [motivo, setMotivo] = useState('Guasto');
  const [note, setNote] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCodiceRicerca('');
      setNuovoArticolo(null);
      setManuale(false);
      setManualeForm({ matricola: '', marca: '', modello: '' });
      setMotivo('Guasto');
      setNote('');
    }
  }, [open]);

  const cercaInMagazzino = async (codice) => {
    const clean = String(codice || '').trim();
    if (!clean) return;
    try {
      const { data } = await axios.get(`${API}/magazzino/scan/${encodeURIComponent(clean)}`);
      if (data.stato === 'venduto') {
        toast.error(`⚠️ Questa cassa è già assegnata a ${data.venduto_cliente_nome || 'un altro cliente'}`);
        return;
      }
      if (data.stato === 'reso' || data.stato === 'dismesso') {
        toast.error(`⚠️ Questa cassa risulta "${data.stato}" — non assegnabile`);
        return;
      }
      setNuovoArticolo(data);
      setManuale(false);
      toast.success(`Trovata in magazzino: ${data.descrizione}`);
    } catch (error) {
      if (error?.response?.status === 404) {
        toast.info(`"${clean}" non in magazzino — inserimento manuale`);
        setNuovoArticolo(null);
        setManuale(true);
        setManualeForm((prev) => ({ ...prev, matricola: clean }));
      } else {
        toast.error(parseApiError(error, 'Errore ricerca magazzino'));
      }
    }
  };

  const handleSostituisci = async () => {
    const payload = { motivo, note: note || null };
    if (nuovoArticolo) {
      payload.nuovo_articolo_id = nuovoArticolo.id;
    } else if (manuale && manualeForm.matricola.trim()) {
      payload.nuova_matricola = manualeForm.matricola.trim();
      payload.nuova_marca = manualeForm.marca || null;
      payload.nuovo_modello = manualeForm.modello || null;
    } else {
      toast.error('Scansiona o inserisci la matricola della nuova cassa');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/impianti/${impianto.id}/sostituisci`, payload);
      let msg = `✅ Cassa sostituita: ${data.vecchia_matricola || 'n/d'} → ${data.nuova_matricola}`;
      if (data.vecchio_articolo_reso) msg += ' • vecchia cassa resa in magazzino';
      if (data.nuovo_articolo_assegnato) msg += ' • nuova cassa scaricata dal magazzino';
      toast.success(msg, { duration: 6000 });
      onDone?.(data);
      onOpenChange(false);
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nella sostituzione'));
    } finally {
      setSaving(false);
    }
  };

  const canSave = !saving && (nuovoArticolo || (manuale && manualeForm.matricola.trim()));

  if (!impianto) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg rounded-none max-h-[90vh] overflow-y-auto" data-testid="sostituisci-cassa-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono uppercase tracking-wider">
              <RefreshCw className="h-5 w-5 text-cyan-500" />
              Sostituisci Cassa
            </DialogTitle>
            <DialogDescription>
              La vecchia cassa torna in magazzino come "resa", la nuova viene assegnata al cliente.
            </DialogDescription>
          </DialogHeader>

          {/* Cassa attuale */}
          <div className="p-3 bg-muted border border-border text-sm" data-testid="sostituisci-cassa-attuale">
            <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Cassa attuale</div>
            <div className="font-mono font-semibold">{impianto.matricola || 'n/d'}</div>
            <div className="text-xs text-muted-foreground">{[impianto.marca, impianto.modello].filter(Boolean).join(' · ') || impianto.tipo}</div>
          </div>

          <div className="flex items-center justify-center text-muted-foreground">
            <ArrowRight className="h-5 w-5" />
          </div>

          {/* Nuova cassa */}
          <div className="space-y-3">
            <Label className="text-xs font-mono uppercase">Nuova cassa — scansiona o digita matricola / EAN</Label>
            <div className="flex gap-2">
              <Input
                value={codiceRicerca}
                onChange={(e) => setCodiceRicerca(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); cercaInMagazzino(codiceRicerca); } }}
                placeholder="Es. 99IEB154034"
                className="rounded-none font-mono flex-1"
                autoFocus
                data-testid="sostituisci-codice-input"
              />
              <Button type="button" variant="outline" className="rounded-none shrink-0" onClick={() => cercaInMagazzino(codiceRicerca)} title="Cerca in magazzino" data-testid="sostituisci-cerca-btn">
                <Search className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" className="rounded-none shrink-0" onClick={() => setScannerOpen(true)} title="Scansiona con fotocamera" data-testid="sostituisci-scan-btn">
                <ScanLine className="h-4 w-4" />
              </Button>
            </div>

            {nuovoArticolo && (
              <div className="p-3 border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20 text-sm" data-testid="sostituisci-nuovo-articolo">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold">
                  <Package className="h-4 w-4" />
                  {nuovoArticolo.descrizione}
                </div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  Matricola: {nuovoArticolo.matricola || 'n/d'} · {[nuovoArticolo.marca, nuovoArticolo.modello].filter(Boolean).join(' · ')}
                </div>
                <div className="text-[10px] text-emerald-600 dark:text-emerald-500 mt-1">Verrà scaricata dal magazzino e assegnata al cliente</div>
              </div>
            )}

            {manuale && !nuovoArticolo && (
              <div className="space-y-2 p-3 border border-border bg-muted/40" data-testid="sostituisci-manuale-form">
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Inserimento manuale (non in magazzino)</div>
                <div>
                  <Label className="text-xs font-mono uppercase">Matricola *</Label>
                  <Input
                    value={manualeForm.matricola}
                    onChange={(e) => setManualeForm({ ...manualeForm, matricola: e.target.value })}
                    className="rounded-none font-mono"
                    data-testid="sostituisci-manuale-matricola"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-mono uppercase">Marca</Label>
                    <Input
                      value={manualeForm.marca}
                      onChange={(e) => setManualeForm({ ...manualeForm, marca: e.target.value })}
                      className="rounded-none"
                      placeholder={impianto.marca || 'Es. Epson'}
                      data-testid="sostituisci-manuale-marca"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Modello</Label>
                    <Input
                      value={manualeForm.modello}
                      onChange={(e) => setManualeForm({ ...manualeForm, modello: e.target.value })}
                      className="rounded-none"
                      placeholder={impianto.modello || 'Es. FP81'}
                      data-testid="sostituisci-manuale-modello"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase">Motivo</Label>
                <Select value={motivo} onValueChange={setMotivo}>
                  <SelectTrigger className="rounded-none" data-testid="sostituisci-motivo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {MOTIVI.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Note</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="rounded-none min-h-[38px]"
                  rows={1}
                  data-testid="sostituisci-note"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button
              onClick={handleSostituisci}
              disabled={!canSave}
              className="rounded-none font-mono uppercase"
              data-testid="sostituisci-conferma-btn"
            >
              {saving ? 'Sostituzione...' : 'Conferma sostituzione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeScannerModal
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={(code) => { setCodiceRicerca(code); cercaInMagazzino(code); }}
        title="Scansiona la nuova cassa"
      />
    </>
  );
}
